import deepmerge from 'deepmerge';

// Typed helpers for this module (kept intentionally small / non-breaking)
export type Density = 'touch' | 'low' | 'medium' | 'high';
export type SizeVariant = 'regular' | 'stackable';

export type SizeValue = {
  height: number;
};

export type SizeOptions = Record<SizeVariant, Record<Density, SizeValue>>;

export type GetSizeArgs = {
  variant?: SizeVariant;
  density?: Density;
};

export type GetSize = (args?: GetSizeArgs) => Partial<SizeValue>;

export const sizeRamp = {
  touch: {
    s100: 44,
    s200: 60
  },
  low: {
    s100: 36,
    s200: 48
  },
  medium: {
    s100: 28,
    s200: 36
  },
  high: {
    s100: 20,
    s200: 24
  }
};

export const defaultSize: SizeOptions = {
  regular: {
    touch: {
      height: sizeRamp.touch.s100
    },
    low: {
      height: sizeRamp.low.s100
    },
    medium: {
      height: sizeRamp.medium.s100
    },
    high: {
      height: sizeRamp.high.s100
    }
  },
  stackable: {
    touch: {
      height: sizeRamp.touch.s200
    },
    low: {
      height: sizeRamp.low.s200
    },
    medium: {
      height: sizeRamp.medium.s200
    },
    high: {
      height: sizeRamp.high.s200
    }
  }
};

const createGetSize = (options: SizeOptions): GetSize => ({
  variant = 'regular',
  density = 'medium'
}: GetSizeArgs = {}) => {
  const { hasOwnProperty } = Object.prototype;
  if (!hasOwnProperty.call(options, variant) || !hasOwnProperty.call(options[variant], density)) {
    return {};
  }
  return options[variant][density];
};

export type CreateSizeInput =
  | (Partial<{ getSize: GetSize }> & Partial<SizeOptions>)
  | ((ramp: typeof sizeRamp) => Partial<{ getSize: GetSize }> & Partial<SizeOptions>);

export const createSize = (size: CreateSizeInput = {}) => {
  const { getSize: otherGetSize, ...otherOptions } = typeof size == 'function' ? size(sizeRamp) : size;

  const mergedOptions = deepmerge(defaultSize, otherOptions as Partial<SizeOptions>, {
    clone: false
  }) as SizeOptions;

  return {
    getSize: otherGetSize || createGetSize(mergedOptions),
    ...mergedOptions
  };
};
